import React from 'react';
import type { DebriefCardsProps } from '../../turncard/types';
import type { DebriefOutcome, DebriefPart } from '../../hooks/useAssessmentApi';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import styles from './DebriefCards.module.css';

const OUTCOMES: Record<DebriefOutcome, { icon: string; label: string }> = {
  handled: { icon: '✅', label: INTERFACE_TEXT.handledLabel },
  took_a_few_tries: { icon: '🔁', label: INTERFACE_TEXT.tookAFewTriesLabel },
  not_handled: { icon: '◌', label: INTERFACE_TEXT.notHandledLabel },
};

// BR-024: "handled with help" wins over "took a few tries"; a part never handled stays not handled.
function outcomeFor(part: DebriefPart): { icon: string; label: string } {
  if (part.handled_with_help && part.outcome !== 'not_handled') {
    return { icon: '💬', label: INTERFACE_TEXT.handledWithHelpLabel };
  }
  return OUTCOMES[part.outcome];
}

function practicedText(n: number): string {
  return n === 1 ? '1 phrase practised' : `${n} phrases practised`;
}

export const DebriefCards: React.FC<DebriefCardsProps> = ({
  mode,
  debrief,
  debriefLoading,
  practiceSummary,
  onBackToToday,
  onStartCheckIn,
  onNextLesson,
  nextLessonStarting = false,
}) => {
  const practice = mode === 'practice';
  const eyebrow =
    mode === 'checkin'
      ? INTERFACE_TEXT.tsDebriefCheckinEyebrow
      : INTERFACE_TEXT.tsDebriefLessonEyebrow;
  return (
    <div className={styles.deb} data-testid="lesson-completion">
      {practice ? (
        <>
          <h2 className={styles.heading}>{INTERFACE_TEXT.tsPracticeDone}</h2>
          {practiceSummary && (
            <p data-testid="practice-summary">
              {practicedText(practiceSummary.phrases_practiced)}
              {', '}
              {practiceSummary.first_try} on the first try
            </p>
          )}
        </>
      ) : (
        <>
          <span className={styles.eyebrow}>{eyebrow}</span>
          <h2 className={styles.heading}>{INTERFACE_TEXT.tsDebriefHeading}</h2>
          {debriefLoading && (
            <p role="status" className={styles.note}>
              {INTERFACE_TEXT.tsDebriefLoading}
            </p>
          )}
          {!debriefLoading && debrief && debrief.parts.length > 0 && (
            <>
              <ul
                className={styles.parts}
                aria-label={INTERFACE_TEXT.tsDebriefPartsLabel}
                data-testid="debrief-parts"
              >
                {debrief.parts.map((part) => {
                  const { icon, label } = outcomeFor(part);
                  return (
                    <li key={part.id} className={styles.part}>
                      <span className={styles.partLabel}>{part.label}</span>
                      <span className={styles.pills}>
                        <span className={styles.pill}>
                          <span aria-hidden="true">{icon}</span> {label}
                        </span>
                        {part.form_note && (
                          <span className={styles.pill}>
                            {INTERFACE_TEXT.noteOnFormLabel}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className={styles.note}>{INTERFACE_TEXT.tsDebriefInfoOnly}</p>
            </>
          )}
        </>
      )}
      {!practice && mode === 'lesson' && onStartCheckIn && (
        <button type="button" className={styles.cta} onClick={onStartCheckIn}>
          {INTERFACE_TEXT.tsStartCheckIn}
        </button>
      )}
      {mode === 'checkin' && onNextLesson && (
        <button
          type="button"
          className={styles.cta}
          onClick={onNextLesson}
          disabled={nextLessonStarting}
        >
          {nextLessonStarting
            ? INTERFACE_TEXT.tsNextLessonStarting
            : INTERFACE_TEXT.tsNextLesson}
        </button>
      )}
      <button type="button" className={styles.alt} onClick={onBackToToday}>
        {INTERFACE_TEXT.tsBackToToday}
      </button>
    </div>
  );
};

export default DebriefCards;
